"use client";

import { useCallback, useEffect, useState } from "react";

export interface CookieOptions {
  /** Number of days until expiration (default: 7) */
  days?: number;
  /** Cookie path (default: "/") */
  path?: string;
  /** SameSite attribute (default: "Lax") */
  sameSite?: "Strict" | "Lax" | "None";
  /** Secure flag - only send over HTTPS (default: true in production) */
  secure?: boolean;
}

const DEFAULT_OPTIONS: Required<CookieOptions> = {
  days: 7,
  path: "/",
  sameSite: "Lax",
  secure:
    typeof window !== "undefined" && window.location.protocol === "https:",
};

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length === 2) {
    return parts.pop()?.split(";").shift() || null;
  }

  return null;
}

/**
 * Set a cookie with the given name, value, and options
 */
export function setCookie(
  name: string,
  value: string,
  options: CookieOptions = {},
): void {
  if (typeof document === "undefined") return;

  const opts = { ...DEFAULT_OPTIONS, ...options };
  const expires = new Date();
  expires.setTime(expires.getTime() + opts.days * 24 * 60 * 60 * 1000);

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  cookieString += `;expires=${expires.toUTCString()}`;
  cookieString += `;path=${opts.path}`;
  cookieString += `;SameSite=${opts.sameSite}`;

  if (opts.secure) {
    cookieString += ";Secure";
  }

  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is async and unsupported in Safari/Firefox; this hook needs a synchronous write.
  document.cookie = cookieString;
}

/**
 * Remove a cookie by name
 */
export function removeCookie(name: string, path = "/"): void {
  if (typeof document === "undefined") return;

  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is async and unsupported in Safari/Firefox; this hook needs a synchronous write.
  document.cookie = `${encodeURIComponent(name)}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}`;
}

/**
 * React hook for managing a cookie with reactive state
 *
 * @param name - Cookie name
 * @param options - Cookie options (days, path, sameSite, secure)
 * @returns [value, setValue, removeValue, isLoaded] - Current value, setter, remove function, and loaded state
 *
 * @example
 * ```tsx
 * const [theme, setTheme, removeTheme, isLoaded] = useCookie("theme", { days: 30 });
 *
 * if (!isLoaded) return <Loading />;
 *
 * // Set cookie
 * setTheme("dark");
 *
 * // Remove cookie
 * removeTheme();
 * ```
 */
export function useCookie(
  name: string,
  options: CookieOptions = {},
): [string | null, (value: string) => void, () => void, boolean] {
  const [value, setValue] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Read cookie on mount (client-side only)
  useEffect(() => {
    setValue(getCookie(name));
    setIsLoaded(true);
  }, [name]);

  // Setter function that updates both cookie and state
  const updateCookie = useCallback(
    (newValue: string) => {
      setCookie(name, newValue, options);
      setValue(newValue);
    },
    [name, options],
  );

  // Remove function that deletes cookie and clears state
  const deleteCookie = useCallback(() => {
    removeCookie(name, options.path);
    setValue(null);
  }, [name, options.path]);

  return [value, updateCookie, deleteCookie, isLoaded];
}

/**
 * Simple hook to check if a cookie exists (boolean flag pattern)
 *
 * @param name - Cookie name
 * @param options - Cookie options
 * @returns [hasFlag, setFlag, isLoaded] - Boolean state, setter, and loaded state
 *
 * @example
 * ```tsx
 * const [hasSeenSplash, setHasSeenSplash, isLoaded] = useCookieFlag("splash_seen", { days: 7 });
 *
 * // Don't render until loaded
 * if (!isLoaded) return null;
 *
 * // Now hasSeenSplash is definitively true or false
 * if (!hasSeenSplash) {
 *   // Show splash
 *   setHasSeenSplash(true);
 * }
 * ```
 */
export function useCookieFlag(
  name: string,
  options: CookieOptions = {},
): [boolean, (value: boolean) => void, boolean] {
  const [cookieValue, setCookieValue, removeCookie, isLoaded] = useCookie(
    name,
    options,
  );

  // Convert to boolean: true if cookie exists and equals "true"
  const hasFlag = cookieValue === "true";

  const setFlag = useCallback(
    (value: boolean) => {
      if (value) {
        setCookieValue("true");
      } else {
        removeCookie();
      }
    },
    [setCookieValue, removeCookie],
  );

  return [hasFlag, setFlag, isLoaded];
}
